/**
 * Конфигурация прокси для внешних сервисов
 * Теперь поддерживает динамическую загрузку настроек из базы данных
 */

import { loadDynamicConfig, createDynamicServicesConfig, type DynamicServicesConfig } from './dynamicProxy';
import { SUPABASE_URL } from '@/utils/supabaseConfig';

// Типы конфигурации прокси (сохраняем для обратной совместимости)
export interface ProxyConfig {
  enabled: boolean;
  baseUrl: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface ExternalServicesConfig {
  supabase: {
    url: string;
    proxyConfig?: ProxyConfig;
  };
  supabaseStorage: {
    baseUrl: string;
    proxyConfig?: ProxyConfig;
  };
  googleMaps: {
    apiUrl: string;
    proxyConfig?: ProxyConfig;
  };
  geocoding: {
    apiUrl: string;
    proxyConfig?: ProxyConfig;
  };
  webhooks: {
    enabled: boolean;
    proxyConfig?: ProxyConfig;
  };
  makeWebhooks: {
    enabled: boolean;
    proxyConfig?: ProxyConfig;
  };
  general: {
    defaultTimeout: number;
    retryAttempts: number;
  };
}

// Статическая конфигурация как fallback
const getEnvVar = (key: string, fallback: string): string => {
  if (typeof window !== 'undefined') {
    return (window as any).ENV?.[key] || fallback;
  }
  return process.env[key] || fallback;
};

const getBooleanEnvVar = (key: string, fallback: boolean): boolean => {
  const value = getEnvVar(key, fallback.toString());
  return value.toLowerCase() === 'true';
};

// Статическая конфигурация (используется как fallback)
export const externalServicesConfig: ExternalServicesConfig = {
  supabase: {
    url: SUPABASE_URL, // Используем параметризованный URL
    proxyConfig: getBooleanEnvVar('VITE_SUPABASE_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_SUPABASE_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_SUPABASE_PROXY_TIMEOUT', '30000')),
      headers: {
        'X-Proxy-Target': 'supabase'
      }
    } : undefined
  },
  supabaseStorage: {
    baseUrl: SUPABASE_URL, // Используем параметризованный URL
    proxyConfig: getBooleanEnvVar('VITE_SUPABASE_STORAGE_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_SUPABASE_STORAGE_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_SUPABASE_STORAGE_PROXY_TIMEOUT', '30000')),
      headers: {
        'X-Proxy-Target': 'supabase-storage'
      }
    } : undefined
  },
  googleMaps: {
    apiUrl: getEnvVar('VITE_GOOGLE_MAPS_API_URL', 'https://maps.googleapis.com'),
    proxyConfig: getBooleanEnvVar('VITE_GOOGLE_MAPS_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_GOOGLE_MAPS_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_GOOGLE_MAPS_PROXY_TIMEOUT', '15000')),
      headers: {
        'X-Proxy-Target': 'google-maps'
      }
    } : undefined
  },
  geocoding: {
    apiUrl: getEnvVar('VITE_GEOCODING_API_URL', 'https://maps.googleapis.com'),
    proxyConfig: getBooleanEnvVar('VITE_GEOCODING_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_GEOCODING_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_GEOCODING_PROXY_TIMEOUT', '15000')),
      headers: {
        'X-Proxy-Target': 'geocoding'
      }
    } : undefined
  },
  webhooks: {
    enabled: getBooleanEnvVar('VITE_WEBHOOKS_ENABLED', true),
    proxyConfig: getBooleanEnvVar('VITE_WEBHOOKS_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_WEBHOOKS_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_WEBHOOKS_PROXY_TIMEOUT', '10000')),
      headers: {
        'X-Proxy-Target': 'webhook'
      }
    } : undefined
  },
  makeWebhooks: {
    enabled: getBooleanEnvVar('VITE_MAKE_WEBHOOKS_ENABLED', true),
    proxyConfig: getBooleanEnvVar('VITE_MAKE_WEBHOOKS_PROXY_ENABLED', false) ? {
      enabled: true,
      baseUrl: getEnvVar('VITE_MAKE_WEBHOOKS_PROXY_URL', ''),
      timeout: parseInt(getEnvVar('VITE_MAKE_WEBHOOKS_PROXY_TIMEOUT', '10000')),
      headers: {
        'X-Proxy-Target': 'make-webhook'
      }
    } : undefined
  },
  general: {
    defaultTimeout: parseInt(getEnvVar('VITE_API_DEFAULT_TIMEOUT', '30000')),
    retryAttempts: parseInt(getEnvVar('VITE_API_RETRY_ATTEMPTS', '3'))
  }
};

// Динамическая конфигурация
let dynamicConfig: DynamicServicesConfig | null = null;

/**
 * Получает актуальную конфигурацию сервисов (динамическую или статическую)
 */
export const getCurrentServicesConfig = async (): Promise<DynamicServicesConfig> => {
  try {
    const config = await loadDynamicConfig();
    dynamicConfig = createDynamicServicesConfig(config);
    return dynamicConfig;
  } catch (error) {
    console.warn('Не удалось загрузить динамическую конфигурацию, используется статическая:', error);
    return externalServicesConfig as DynamicServicesConfig;
  }
};

/**
 * Проверяет, включен ли прокси для сервиса (с поддержкой динамических настроек)
 */
export const isProxyEnabled = async (service: keyof Omit<ExternalServicesConfig, 'general'>): Promise<boolean> => {
  try {
    const config = await getCurrentServicesConfig();
    return config[service].proxyConfig?.enabled || false;
  } catch (error) {
    // Fallback к статической конфигурации
    const serviceConfig = externalServicesConfig[service];
    return serviceConfig.proxyConfig?.enabled || false;
  }
};

/**
 * Получает URL сервиса (с поддержкой динамических настроек)
 */
export const getServiceUrl = async (service: keyof Omit<ExternalServicesConfig, 'general'>): Promise<string> => {
  try {
    const config = await getCurrentServicesConfig();
    const serviceConfig = config[service];
    
    if (serviceConfig.proxyConfig?.enabled && serviceConfig.proxyConfig.baseUrl) {
      return serviceConfig.proxyConfig.baseUrl;
    }
    
    if ('url' in serviceConfig) {
      return serviceConfig.url;
    }
    
    if ('apiUrl' in serviceConfig) {
      return serviceConfig.apiUrl;
    }
    
    if ('baseUrl' in serviceConfig) {
      return serviceConfig.baseUrl;
    }
    
    throw new Error(`Не удалось получить URL для сервиса ${service}`);
  } catch (error) {
    // Fallback к статической конфигурации
    const serviceConfig = externalServicesConfig[service];
    
    if (serviceConfig.proxyConfig?.enabled && serviceConfig.proxyConfig.baseUrl) {
      return serviceConfig.proxyConfig.baseUrl;
    }
    
    if ('url' in serviceConfig) {
      return serviceConfig.url;
    }
    
    if ('apiUrl' in serviceConfig) {
      return serviceConfig.apiUrl;
    }
    
    if ('baseUrl' in serviceConfig) {
      return serviceConfig.baseUrl;
    }
    
    throw new Error(`Не удалось получить URL для сервиса ${service}`);
  }
};

/**
 * Получает заголовки прокси (с поддержкой динамических настроек)
 */
export const getProxyHeaders = async (service: keyof Omit<ExternalServicesConfig, 'general'>): Promise<Record<string, string>> => {
  try {
    const config = await getCurrentServicesConfig();
    return config[service].proxyConfig?.headers || {};
  } catch (error) {
    // Fallback к статической конфигурации
    const serviceConfig = externalServicesConfig[service];
    return serviceConfig.proxyConfig?.headers || {};
  }
};

/**
 * Получает таймаут сервиса (с поддержкой динамических настроек)
 */
export const getServiceTimeout = async (service: keyof Omit<ExternalServicesConfig, 'general'>): Promise<number> => {
  try {
    const config = await getCurrentServicesConfig();
    return config[service].proxyConfig?.timeout || config.general.defaultTimeout;
  } catch (error) {
    // Fallback к статической конфигурации
    const serviceConfig = externalServicesConfig[service];
    return serviceConfig.proxyConfig?.timeout || externalServicesConfig.general.defaultTimeout;
  }
};

// Синхронные версии функций для обратной совместимости (используют кэш)
export const isProxyEnabledSync = (service: keyof Omit<ExternalServicesConfig, 'general'>): boolean => {
  if (dynamicConfig) {
    return dynamicConfig[service].proxyConfig?.enabled || false;
  }
  return externalServicesConfig[service].proxyConfig?.enabled || false;
};

export const getServiceUrlSync = (service: keyof Omit<ExternalServicesConfig, 'general'>): string => {
  let serviceConfig: any;
  
  if (dynamicConfig) {
    serviceConfig = dynamicConfig[service];
  } else {
    serviceConfig = externalServicesConfig[service];
  }
  
  if (serviceConfig.proxyConfig?.enabled && serviceConfig.proxyConfig.baseUrl) {
    return serviceConfig.proxyConfig.baseUrl;
  }
  
  if ('url' in serviceConfig) {
    return serviceConfig.url;
  }
  
  if ('apiUrl' in serviceConfig) {
    return serviceConfig.apiUrl;
  }
  
  if ('baseUrl' in serviceConfig) {
    return serviceConfig.baseUrl;
  }
  
  throw new Error(`Не удалось получить URL для сервиса ${service}`);
};

export const getProxyHeadersSync = (service: keyof Omit<ExternalServicesConfig, 'general'>): Record<string, string> => {
  if (dynamicConfig) {
    return dynamicConfig[service].proxyConfig?.headers || {};
  }
  return externalServicesConfig[service].proxyConfig?.headers || {};
};

export const getServiceTimeoutSync = (service: keyof Omit<ExternalServicesConfig, 'general'>): number => {
  if (dynamicConfig) {
    return dynamicConfig[service].proxyConfig?.timeout || dynamicConfig.general.defaultTimeout;
  }
  return externalServicesConfig[service].proxyConfig?.timeout || externalServicesConfig.general.defaultTimeout;
};

// Логирование конфигурации (только в development)
if (getEnvVar('NODE_ENV', 'development') === 'development') {
  getCurrentServicesConfig().then(config => {
    console.log('🌐 Динамическая конфигурация внешних сервисов загружена:', {
      supabaseProxy: config.supabase.proxyConfig?.enabled,
      supabaseStorageProxy: config.supabaseStorage.proxyConfig?.enabled,
      googleMapsProxy: config.googleMaps.proxyConfig?.enabled,
      geocodingProxy: config.geocoding.proxyConfig?.enabled,
      webhooksProxy: config.webhooks.proxyConfig?.enabled,
      makeWebhooksProxy: config.makeWebhooks.proxyConfig?.enabled
    });
  }).catch(() => {
    console.log('🌐 Используется статическая конфигурация внешних сервисов');
  });
}
